/** /agravos/hiv (D-060): gestações com HIV, TARV, notificação e profilaxia do recém-nascido. */
import { ROTULO_PROFILAXIA_RN, type ProfilaxiaArvRn } from '@metaepi/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Alerta,
  CabecalhoPagina,
  Carregando,
  ConteudoPagina,
  EstadoErro,
  EstadoVazio,
  Selecao,
  SeloStatus,
  Tabela,
} from '../../components/ui';
import { mensagemDeErro } from '../../lib/erros';
import { dataBR } from '../pacientes/formatos';
import { FILTRO_FASE, useAgravoHiv, type ItemHiv } from './api';
import { CelulaFase, CelulaGestante, Contador, GradeContadores, SeloNotificacao } from './comum';

const SITUACOES = [
  { valor: '', rotulo: 'Todas as situações' },
  { valor: 'sem_tarv', rotulo: 'Sem TARV registrada' },
  { valor: 'notificacao', rotulo: 'Notificação pendente' },
];

function CelulaRn({ item }: { item: ItemHiv }) {
  if (item.recemNascidos.length === 0) return <span className="text-texto-suave">—</span>;
  return (
    <ul className="flex flex-col gap-1">
      {item.recemNascidos.map((r) => (
        <li key={r.id} className="flex flex-col">
          <Link
            to={`/recem-nascidos/${r.id}`}
            className="font-medium text-primaria hover:underline"
          >
            {r.nome ?? 'Recém-nascido'}
          </Link>
          <span className="text-xs text-texto-suave">
            {r.profilaxia
              ? `Profilaxia: ${ROTULO_PROFILAXIA_RN[r.profilaxia as ProfilaxiaArvRn] ?? r.profilaxia}`
              : 'Profilaxia não informada'}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PaginaHiv() {
  const pagina = useAgravoHiv();
  const [fase, setFase] = useState('');
  const [situacao, setSituacao] = useState('');
  const itens = (pagina.data?.itens ?? []).filter(
    (i) =>
      (!fase || i.fase === fase) &&
      (situacao === 'sem_tarv'
        ? !i.tarvRegistrada
        : situacao === 'notificacao'
          ? i.notificacao !== 'pdf_gerado' && i.notificacao !== 'notificada'
          : true),
  );
  const c = pagina.data?.contadores;

  return (
    <ConteudoPagina>
      <CabecalhoPagina
        ajuda="agenda#agravos"
        titulo="HIV"
        descricao="Gestantes com HIV no seu município: TARV, notificação e profilaxia do recém-nascido. Abra a ficha para ver os detalhes."
      />
      <Alerta
        tipo="info"
        titulo="O seguimento da criança exposta ao HIV está em desenvolvimento."
      />
      {pagina.isPending ? (
        <Carregando linhas={5} />
      ) : pagina.isError ? (
        <EstadoErro
          descricao={mensagemDeErro(pagina.error)}
          aoTentarNovamente={() => void pagina.refetch()}
        />
      ) : pagina.data.precisaMunicipio || !c ? (
        <EstadoVazio
          titulo="Seu usuário não está vinculado a um município."
          descricao="Peça ao administrador para vincular o seu usuário a um município."
        />
      ) : (
        <>
          <GradeContadores>
            <Contador rotulo="Gestantes com HIV" valor={c.gestantes} />
            <Contador rotulo="Sem TARV registrada" valor={c.semTarv} tipo="perigo" />
            <Contador rotulo="Notificação pendente" valor={c.notificacaoPendente} tipo="alerta" />
            <Contador
              rotulo={`Recém-nascidos com profilaxia (de ${c.recemNascidos})`}
              valor={c.recemNascidosComProfilaxia}
            />
          </GradeContadores>
          <div className="grid gap-4 sm:grid-cols-2">
            <Selecao
              rotulo="Fase"
              value={fase}
              onChange={(e) => setFase(e.target.value)}
              opcoes={FILTRO_FASE}
            />
            <Selecao
              rotulo="Situação"
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
              opcoes={SITUACOES}
            />
          </div>
          {itens.length === 0 ? (
            <EstadoVazio
              titulo={
                fase || situacao
                  ? 'Nenhuma gestante com estes filtros.'
                  : 'Nenhuma gestante com HIV em acompanhamento.'
              }
            />
          ) : (
            <>
              <p className="text-sm text-texto-suave">{`${itens.length} ${itens.length === 1 ? 'gestante' : 'gestantes'}.`}</p>
              <Tabela
                legenda="Gestantes com HIV"
                itens={itens}
                chave={(i) => i.id}
                colunas={[
                  {
                    titulo: 'Gestante',
                    celula: (i: ItemHiv) => <CelulaGestante item={i} aba="tratamento" />,
                  },
                  {
                    titulo: 'Fase',
                    celula: (i: ItemHiv) => (
                      <CelulaFase fase={i.fase} dum={i.data_ultima_menstruacao} />
                    ),
                  },
                  {
                    titulo: 'TARV',
                    celula: (i: ItemHiv) =>
                      i.tarvRegistrada ? (
                        <SeloStatus tipo="sucesso">
                          {i.tarvInicio ? `Desde ${dataBR(i.tarvInicio)}` : 'Registrada'}
                        </SeloStatus>
                      ) : (
                        <SeloStatus tipo="perigo">Não registrada</SeloStatus>
                      ),
                  },
                  {
                    titulo: 'Notificação',
                    celula: (i: ItemHiv) => <SeloNotificacao status={i.notificacao} />,
                  },
                  { titulo: 'Recém-nascido', celula: (i: ItemHiv) => <CelulaRn item={i} /> },
                  {
                    titulo: 'Unidade',
                    celula: (i: ItemHiv) => i.unidade_nome,
                    secundaria: true,
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </ConteudoPagina>
  );
}
